import { useMemo } from 'react';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import type { CommunitySpace } from '~/interfaces/community';
import type { loader as communityLoader } from '~/routes/community';

import { useCommunityAccess } from './useCommunityAccess';
import { useCommunityMembership } from './useCommunityMembership';
import { useSpaceAccess } from './useSpaceAccess';

export interface ChannelUpgradeGate {
  isOpen: boolean;
  space: CommunitySpace | null;
}

/**
 * Resolves the `space_id` query param against the layout's spaces list and decides
 * whether the channel-upgrade modal should be open for it.
 *
 * Channel mode only for a member with community access facing a still-locked,
 * known channel they can upgrade into; anything else ignores the param (no modal).
 */
export const useChannelUpgradeGate = (): ChannelUpgradeGate => {
  const layoutData = useRouteLoaderData<typeof communityLoader>('routes/community');
  const { currentModal } = useGlobalModals();
  const [searchParams] = useSearchParams();
  const { isMember } = useCommunityMembership();
  const { isCommunityAccessible } = useCommunityAccess();
  const { canAccessSpace, isSpaceConditionLocked } = useSpaceAccess();

  const spaceIdParam = searchParams.get('space_id');
  const space = useMemo(
    () => (spaceIdParam ? (layoutData?.spaces?.find((s) => s.id === spaceIdParam) ?? null) : null),
    [spaceIdParam, layoutData?.spaces],
  );

  const isOpen =
    currentModal === 'community_channel_upgrade' &&
    isMember &&
    isCommunityAccessible &&
    space != null &&
    !canAccessSpace(space) &&
    !isSpaceConditionLocked(space);

  return { isOpen, space };
};
